import dayjs from '@srm/ui/lib/dayjs';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';

// Формат-строки dayjs для RU-дат документов; строгий парс — на padded-значениях.
// HH.mm.ss — легаси-разделитель времени из старых данных.
const RU_DATE = 'DD.MM.YYYY';
const RU_DATE_TIME = 'DD.MM.YYYY HH:mm:ss';
const RU_DATE_TIME_DOTS = 'DD.MM.YYYY HH.mm.ss';

// date-fns parse принимал 1–2 цифры в любом компоненте (день/месяц/часы/минуты/секунды),
// строгий dayjs-токен — нет. Нормализуем ввод: zero-pad'им однозначные числовые группы,
// затем парсим строго. Так '2.11.2024 9:05:00' читается как 2 ноября, а не уходит в
// fallback new Date(), где V8 трактует '2.11.2024' как 11 февраля (MM.DD, silent-wrong).
const padRuDateComponents = (value: string): string =>
    value.trim().replace(/\d+/g, (n) => (n.length === 1 ? `0${n}` : n));

/**
 * Безопасное форматирование даты: возвращает '' вместо RangeError на невалидном значении.
 * fmt — в токенах dayjs (DD.MM.YYYY, HH:mm:ss и т. п.).
 */
export const safeFormatDate = (value: Date | string | number | null | undefined, fmt: string): string => {
    if (value == null) return '';
    const d = dayjs(toLocalDate(value));
    return d.isValid() ? d.format(fmt) : '';
};

/** Календарных дней от `date` до `now` по локальной полуночи (не по 24-часовым периодам). */
export const diffCalendarDays = (date: Date | string | number, now: Date | string | number): number =>
    dayjs(now).startOf('day').diff(dayjs(date).startOf('day'), 'day');

/** «5 марта»; год — только если не текущий: «5 марта 2025 г.». */
export function formatDayMonth(date: Date | string | number, now: Date | string | number): string {
    const d = dayjs(date);
    return d.locale('ru').format(d.year() === dayjs(now).year() ? 'D MMMM' : 'D MMMM YYYY [г.]');
}

/**
 * Парсит RU-дату dd.MM.yyyy (1–2 цифры дня/месяца, строгий режим) в Date.
 * null на невалидном вводе. Заменяет date-fns parse(str, 'dd.MM.yyyy', new Date()).
 */
export const parseRuDate = (value: string): Date | null => {
    const d = dayjs(padRuDateComponents(value), RU_DATE, true);
    return d.isValid() ? d.toDate() : null;
};

/**
 * Парсит RU-дату-время DD.MM.YYYY HH:mm:ss (строгий режим) в Date. Пробует сначала
 * разделитель ':' , затем легаси '.' (HH.mm.ss). null на невалидном вводе.
 */
export const parseRuDateTime = (value: string): Date | null => {
    const v = padRuDateComponents(value);
    let d = dayjs(v, RU_DATE_TIME, true);
    if (!d.isValid()) d = dayjs(v, RU_DATE_TIME_DOTS, true);
    return d.isValid() ? d.toDate() : null;
};

/**
 * ISO → локализованное «DD.MM.YYYY HH:MM» (ru-RU). На null/невалид возвращает '—'.
 * Используется в списках устройств/токенов, где нужен компактный read-only вывод.
 */
export const formatRuDateTime = (value: string | number | Date | null | undefined): string => {
    if (value == null) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/**
 * Метка времени бэкенда, у которой зона может быть опущена («2026-05-21T10:44:00» — это UTC):
 * конструктор Date трактует такую строку как локальное время, поэтому без зоны дописываем «Z».
 * null — пусто или не дата.
 */
export const parseUtcTimestamp = (raw: string | null | undefined): Date | null => {
    if (!raw) return null;
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
    const parsed = new Date(hasTimezone ? raw : `${raw}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const dateToISODateTimeNoZ = (date: Date | undefined | null) => {
    if (!date) return '';
    const d = dayjs(date);
    // На Invalid Date date-fns бросал RangeError, dayjs вернул бы строку "Invalid Date";
    // не отдаём её в payload — возвращаем '' как для пустого значения.
    return d.isValid() ? d.format('YYYY-MM-DD[T]HH:mm:ss[.00]') : '';
};

export function stringifyDates<T>(value: T): T {
    if (value == null) return value;

    if (value instanceof Date) {
        return value.toISOString() as unknown as T;
    }

    if (Array.isArray(value)) {
        return value.map((v) => stringifyDates(v)) as unknown as T;
    }

    if (typeof value === 'object') {
        const result: any = {};
        for (const [key, val] of Object.entries(value as any)) {
            result[key] = stringifyDates(val);
        }
        return result;
    }

    return value;
}

export { dateToISODateTimeNoZ };
